/**
 * Template Selector - Template filtering, search, and selection UI
 */

import React, { useMemo } from 'react';
import { Box, Flex, Text, TextField, Select, Badge, Card } from '@radix-ui/themes';
import { CubeIcon } from '@radix-ui/react-icons';
import { DeviceTemplate, DeviceType } from '../../types';
import { DEVICE_TYPE_LABELS } from '../../config/constants';
import DeviceIcon from '../DeviceIcon';

interface TemplateSelectorProps {
  templates: DeviceTemplate[];
  selectedTemplateId: string | null;
  onSelectTemplate: (templateId: string | null) => void;
  filterType: DeviceType | 'all';
  onFilterTypeChange: (type: DeviceType | 'all') => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  templates,
  selectedTemplateId,
  onSelectTemplate,
  filterType,
  onFilterTypeChange,
  searchQuery,
  onSearchQueryChange,
}) => {
  // Filter templates based on selected type and search query
  const filteredTemplates = useMemo(() => {
    let filtered = templates;
    
    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter((t: DeviceTemplate) => t.type === filterType);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((t: DeviceTemplate) => 
        t.name.toLowerCase().includes(query) ||
        t.brand?.toLowerCase().includes(query) ||
        t.model?.toLowerCase().includes(query) ||
        t.description?.toLowerCase().includes(query)
      );
    }
    
    // Sort alphabetically by name
    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [templates, filterType, searchQuery]);

  // Group templates by brand for organized display
  const templatesByBrand = useMemo(() => {
    const grouped = new Map<string, DeviceTemplate[]>();
    filteredTemplates.forEach(template => {
      const brand = template.brand || 'Other';
      if (!grouped.has(brand)) {
        grouped.set(brand, []);
      }
      grouped.get(brand)!.push(template);
    });
    // Sort brands alphabetically
    const sortedEntries = Array.from(grouped.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    return new Map(sortedEntries);
  }, [filteredTemplates]);

  // Get selected template object
  const selectedTemplate = useMemo(() => {
    if (!selectedTemplateId) return null;
    return templates.find(t => t.id === selectedTemplateId) || null;
  }, [selectedTemplateId, templates]);

  return (
    <Flex direction="column" gap="4">
      <Box>
        <Text as="label" size="2" weight="bold" mb="2">
          Filter by Type
        </Text>
        <Select.Root value={filterType} onValueChange={(value) => onFilterTypeChange(value as DeviceType | 'all')}>
          <Select.Trigger style={{ width: '100%' }} mt="1" />
          <Select.Content>
            <Select.Item value="all">All Devices</Select.Item>
            <Select.Separator />
            {Object.entries(DEVICE_TYPE_LABELS).map(([value, label]) => (
              <Select.Item key={value} value={value}>
                <Flex align="center" gap="2">
                  <DeviceIcon type={value as DeviceType} size={16} />
                  <span>{label}</span>
                </Flex>
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      </Box>

      <Box>
        <Text as="label" size="2" weight="bold" mb="2">
          Search Templates
        </Text>
        <TextField.Root
          placeholder="🔍 Search by name, brand, model..."
          value={searchQuery}
          onChange={(e) => onSearchQueryChange(e.target.value)}
          mt="1"
        />
      </Box>

      <Box>
        <Flex justify="between" align="center" mb="2">
          <Text size="2" weight="bold">Select Device</Text>
          {selectedTemplate && (
            <Badge color="violet">
              <CubeIcon /> {selectedTemplate.brand || 'Generic'}
            </Badge>
          )}
        </Flex>
        
        <Box style={{ 
          maxHeight: '280px', 
          overflowY: 'auto', 
          border: '1px solid var(--gray-6)', 
          borderRadius: 'var(--radius-2)',
          padding: 'var(--space-2)'
        }}>
          {templatesByBrand.size === 0 ? (
            <Text size="2" color="gray" style={{ display: 'block', textAlign: 'center', padding: 'var(--space-4)' }}>
              No templates found. Try adjusting your filters or search.
            </Text>
          ) : (
            Array.from(templatesByBrand.entries()).map(([brand, brandTemplates]) => (
              <Box key={brand} mb="3">
                <Text size="1" weight="bold" color="gray" mb="1" style={{ display: 'block' }}>
                  {brand}
                </Text>
                <Flex direction="column" gap="1">
                  {brandTemplates.map((template: DeviceTemplate) => (
                    <Card
                      key={template.id}
                      style={{ 
                        cursor: 'pointer',
                        backgroundColor: selectedTemplateId === template.id 
                          ? 'var(--violet-3)' 
                          : 'var(--gray-2)',
                        border: selectedTemplateId === template.id 
                          ? '2px solid var(--violet-9)' 
                          : '1px solid var(--gray-6)',
                      }}
                      onClick={() => onSelectTemplate(template.id)}
                    >
                      <Flex align="center" gap="2">
                        <DeviceIcon type={template.type} size={24} />
                        <Box style={{ flex: 1 }}>
                          <Text size="2" weight="bold">{template.name}</Text>
                          {template.description && (
                            <Text size="1" color="gray" style={{ display: 'block' }}>
                              {template.description}
                            </Text>
                          )}
                        </Box>
                        {template.isBuiltIn && (
                          <Badge size="1" color="blue">Built-in</Badge>
                        )}
                      </Flex>
                    </Card>
                  ))}
                </Flex>
              </Box>
            ))
          )}
        </Box>
      </Box>
    </Flex>
  );
};

export default TemplateSelector;
